import { tr } from 'mmfintech-commons'
import { useAutoClearError, useStablePreview, type useWithdrawPreviewMutation } from 'mmfintech-backend-api'
import type { PaymentPreview, PaymentWithdrawalRequest, WithdrawalSubFlow } from 'mmfintech-backend-api/types'

/**
 * Type for RTK Query withdrawal preview mutation hooks
 * Accepts hooks like useWithdrawPreviewMutation, useInternalTransferPreviewMutation, etc.
 */
type WithdrawalPreviewMutationHook = typeof useWithdrawPreviewMutation

interface UseWithdrawPromoCodeParams {
  /**
   * RTK Query mutation hook to use for validation
   * @example useWithdrawPreviewMutation
   */
  mutation: WithdrawalPreviewMutationHook
  /** Sub-flow identifier (e.g., 'BANKWIRE', 'CRYPTO', 'PAY_TO_CARD') */
  subFlow: WithdrawalSubFlow | string
  /** Preview data from parent query */
  previewData: PaymentPreview | undefined
  /** Function to build request data from promo code */
  buildRequest: (code: string) => PaymentWithdrawalRequest
  /**
   * Callback on successful promo code application
   * Can be synchronous (e.g., setState) or asynchronous
   * @example onSuccess: setPromoCode (no wrapper needed!)
   */
  onSuccess?: (code: string) => void | Promise<void>
}

interface UseWithdrawPromoCodeReturn {
  /** Stable preview data (doesn't nullify on errors) */
  displayPreview: PaymentPreview | undefined
  /** Handler to validate promo code */
  validatePromoCode: (code: string) => Promise<void>
  /** Loading state from RTK Query */
  isLoading: boolean
  /** Error message (auto-clears after 5s) */
  errorMessage: string
}

/**
 * Manages promo code validation for withdraw flows
 *
 * Combines stable preview state, error auto-clearing, and validation logic
 * into a reusable hook for all withdraw flows (BankTransfer, Crypto, etc.)
 *
 * @example
 * ```TypeScript
 * const { displayPreview, validatePromoCode, isLoading, errorMessage } = useWithdrawPromoCode({
 *   mutation: useWithdrawPreviewMutation,
 *   subFlow: 'BANKWIRE',
 *   previewData: transferPreview,
 *   buildRequest: (code) => ({
 *     ...formValues.prepare(),
 *     amount: parseFloat(formValues.getValue('amount')),
 *     sourceAccountId: selectedAccount?.id,
 *     destinationPaymentInstrumentId: selectedRecipient?.paymentInstrumentId,
 *     promoCode: code
 *   }),
 *   onSuccess: onApplyPromoCode
 * });
 * ```
 */
export const useWithdrawPromoCode = ({
  mutation,
  subFlow,
  previewData,
  buildRequest,
  onSuccess
}: UseWithdrawPromoCodeParams): UseWithdrawPromoCodeReturn => {
  // RTK Query mutation for promo validation (provides isLoading, error directly)
  const [validatePromoMutation, { isLoading, error: promoError }] = mutation()

  // Simple hook - maintains stable display state (prevents UI nullification)
  const [displayPreview, setDisplayPreview] = useStablePreview(previewData)

  // Auto-clear error message after 5 seconds (returns [message, clearFn])
  const [errorMessage, clearError] = useAutoClearError(
    promoError,
    tr('FRONTEND.USE_DEPOSIT_PROMO_CODE.INVALID_PROMO_CODE', 'Invalid promo code'),
    5000
  )

  // Handle promo code validation (single API request, updates local state)
  const validatePromoCode = async (code: string): Promise<void> => {
    // Clear previous error message
    clearError()

    // Build request data using provided function
    const requestData = buildRequest(code)

    // Single API request - RTK Query handles isLoading, error states
    const response = await validatePromoMutation({
      data: requestData,
      subFlow: subFlow as WithdrawalSubFlow
    }).unwrap()

    // SUCCESS: Update local display state directly (no second request!)
    setDisplayPreview(response)

    // Store promo code for final submission (no API call)
    if (onSuccess) {
      await onSuccess(code)
    }

    // Error thrown automatically by RTK Query if validation fails
  }

  return {
    displayPreview,
    validatePromoCode,
    isLoading,
    errorMessage
  }
}
